import { CloudUpload } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  LinearProgress,
  Paper,
  Typography
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addInsight } from '../../store/slices/insightsSlice';
import { addInterview } from '../../store/slices/interviewsSlice';
import { RootState } from '../../types';
import { createInterviewFromVtt, generateInsightsFromTranscript, parseVttFile } from '../../utils/vttParser';

interface UploadStatus {
  status: 'idle' | 'uploading' | 'processing' | 'success' | 'error';
  message: string;
  fileName?: string;
}

const VttUploader: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({ status: 'idle', message: '' });
  const [dragOver, setDragOver] = useState(false);

  const handleFileUpload = async (file: File) => {
    if (!file || !file.name.endsWith('.vtt')) {
      setUploadStatus({
        status: 'error',
        message: '.vttファイルを選択してください。'
      });
      return;
    }

    if (!user) {
      setUploadStatus({
        status: 'error',
        message: 'ユーザー情報が取得できません。'
      });
      return;
    }

    setUploadStatus({
      status: 'uploading',
      message: 'ファイルを読み込んでいます...',
      fileName: file.name
    });

    try {
      const fileContent = await file.text();

      setUploadStatus({
        status: 'processing',
        message: 'トランスクリプトを解析しています...',
        fileName: file.name
      });

      // VTTファイルを解析
      const vttEntries = parseVttFile(fileContent);

      if (vttEntries.length === 0) {
        const errorMsg = '有効なトランスクリプトデータが見つかりませんでした。';
        throw new Error(errorMsg);
      }

      // インタビューデータを作成
      const interview = createInterviewFromVtt(vttEntries, file.name, user.name);

      // インサイトを自動生成
      const generatedInsights = generateInsightsFromTranscript(interview);

      // Reduxストアに追加
      dispatch(addInterview(interview));

      // 生成されたインサイトをストアに追加
      generatedInsights.forEach(insightData => {
        const insight = {
          id: Date.now().toString(36) + Math.random().toString(36).substr(2),
          title: insightData.title,
          content: insightData.content,
          summary: insightData.summary,
          tags: [], // 後で手動でタグを追加できる
          interviewId: interview.id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        dispatch(addInsight(insight));
      });

      setUploadStatus({
        status: 'success',
        message: `ファイルが正常に処理されました！インタビュー1件とインサイト${generatedInsights.length}件が作成されました。`,
        fileName: file.name
      });

    } catch (error) {
      console.error('ファイル処理エラー:', error);
      setUploadStatus({
        status: 'error',
        message: error instanceof Error ? error.message : 'ファイル処理中にエラーが発生しました。'
      });
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const resetUploadStatus = () => {
    setUploadStatus({ status: 'idle', message: '' });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        .vttファイルアップロード
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Microsoft Teamsの会議記録（.vttファイル）をアップロードして、自動的にインタビューとインサイトを生成します。
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            sx={{
              border: 2,
              borderStyle: 'dashed',
              borderColor: dragOver ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              backgroundColor: dragOver ? 'action.hover' : 'background.paper',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              }
            }}
          >
            <input
              type="file"
              accept=".vtt"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="file-upload"
              disabled={uploadStatus.status === 'uploading' || uploadStatus.status === 'processing'}
            />
            <label htmlFor="file-upload" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
              <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                .vttファイルをドラッグ&ドロップまたはクリックして選択
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Microsoft Teamsの会議記録ファイル（.vtt形式）をアップロードしてください
              </Typography>
            </label>
          </Box>
        </CardContent>
      </Card>

      {uploadStatus.status !== 'idle' && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          {uploadStatus.status === 'uploading' && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CircularProgress size={20} sx={{ mr: 2 }} />
                <Typography variant="body1">アップロード中...</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                ファイル: {uploadStatus.fileName}
              </Typography>
            </Box>
          )}

          {uploadStatus.status === 'processing' && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CircularProgress size={20} sx={{ mr: 2 }} />
                <Typography variant="body1">処理中...</Typography>
              </Box>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {uploadStatus.message}
              </Typography>
            </Box>
          )}

          {uploadStatus.status === 'success' && (
            <Alert
              severity="success"
              action={
                <Button color="inherit" size="small" onClick={resetUploadStatus}>
                  閉じる
                </Button>
              }
            >
              <Typography variant="body1" fontWeight="bold">
                処理完了
              </Typography>
              <Typography variant="body2">
                {uploadStatus.message}
              </Typography>
            </Alert>
          )}

          {uploadStatus.status === 'error' && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={resetUploadStatus}>
                  閉じる
                </Button>
              }
            >
              <Typography variant="body1" fontWeight="bold">
                エラーが発生しました
              </Typography>
              <Typography variant="body2">
                {uploadStatus.message}
              </Typography>
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default VttUploader;
